import { config } from '@/forms/config';
import type {
  FormConditionalValidation,
  FormField,
  FormInitialValues,
  FormValidations,
} from '@/forms/typings';
import type { ArrayFormField } from '@/forms/typings';
import { registerGroupRestrictionValidations } from '@/forms/utils/createAtLeastOneChildWithValueYup';
import { createAtLeastOneCompleteInstanceYupSchema } from '@/forms/utils/createAtLeastOneCompleteInstanceYup';
import { generateValidations } from '@/forms/utils/generateValidations';
import { getBaseFieldId } from '@/forms/utils/getBaseFieldId';
import { getRequiredChildSuffixes } from '@/forms/utils/getRequiredChildSuffixes';

import type { FormFieldModuleContext } from '../registry/field-module.types';

export function buildArrayInitialState(
  field: FormField,
  values: FormInitialValues,
  validations: FormValidations,
  conditions: FormConditionalValidation[] | undefined,
  ctx: FormFieldModuleContext,
): { initialValues: FormInitialValues; validationFields: FormValidations } {
  let initialValues: FormInitialValues = { ...values };
  let validationFields: FormValidations = { ...validations };

  const f = field as ArrayFormField;

  if (f.notApplicableField) {
    initialValues[f.notApplicableField.fieldId] = f.notApplicableField.value;
  }

  const shouldValidateFieldId = `${f.fieldId}${config.shouldValidateFieldSuffix}`;

  // hasAnyValue counts Not Applicable as a value; an N/A instance seeds false to match
  // what useMultipleField writes for it.
  initialValues[shouldValidateFieldId] = f.hasAnyValue && !f.notApplicable;

  initialValues[`${f.fieldId}${config.requiredFieldSuffix}`] = false;

  // Real-time gate for child required-field validation: maintained by useMultipleField.
  initialValues[`${f.fieldId}${config.instanceHasValueFieldSuffix}`] = f.hasAnyValue;

  const schema = generateValidations(f, conditions);

  if (schema) {
    validationFields[`${f.fieldId}${config.requiredFieldSuffix}`] = schema;
  }

  const combinedConditions: FormConditionalValidation[] = [
    {
      type: 'should-validate',
      relatedFieldId: shouldValidateFieldId,
    },
  ];

  if (conditions) {
    combinedConditions.push(...conditions);
  }

  // Registered even for empty instances: an added-but-untouched instance must still
  // enforce "fill at least one child"; hidden optional arrays stay gated by _shouldValidate.
  registerGroupRestrictionValidations(
    initialValues,
    validationFields,
    f.fieldId,
    f.fields,
    f.restriction,
    combinedConditions,
  );

  // Every present instance (any index) must have its required children filled or be
  // removed — no gating on live instance content. A blank freshly-added instance is
  // invalid until filled or deleted, same as a mandatory-floor instance.
  const instanceConditions: FormConditionalValidation[] = combinedConditions;

  // Always register child schemas regardless of hasAnyValue.
  for (const arrayField of f.fields) {
    const detailValuesAndValidations = ctx.recurse(
      arrayField,
      initialValues,
      validationFields,
      instanceConditions,
    );

    initialValues = {
      ...initialValues,
      ...detailValuesAndValidations.initialValues,
    };
    validationFields = {
      ...validationFields,
      ...detailValuesAndValidations.validationFields,
    };
  }

  // Register collection-level "at least one complete" schema once, at index 0 only.
  if (f.index === 0 && f.isRequired) {
    const baseFieldId = getBaseFieldId(f.fieldId);
    const requiredChildSuffixes = getRequiredChildSuffixes(f.fieldId, f.fields);
    const atLeastOneCompleteKey = `${baseFieldId}${config.atLeastOneCompleteFieldSuffix}`;

    initialValues[atLeastOneCompleteKey] = undefined;
    // Every entry shares one definition, so entry 0's Not Applicable box speaks for the group.
    validationFields[atLeastOneCompleteKey] = createAtLeastOneCompleteInstanceYupSchema(
      baseFieldId,
      requiredChildSuffixes,
      conditions,
      f.notApplicableField !== undefined,
    );
  }

  if (f.naDetails && f.notApplicableField) {
    const combinedNAConditions: FormConditionalValidation[] = [
      { type: 'not-applicable', relatedFieldId: f.notApplicableField.fieldId },
      ...(conditions ?? []),
    ];

    const naValuesAndValidations = ctx.recurse(
      f.naDetails,
      initialValues,
      validationFields,
      combinedNAConditions,
    );

    initialValues = {
      ...initialValues,
      ...naValuesAndValidations.initialValues,
    };

    validationFields = {
      ...validationFields,
      ...naValuesAndValidations.validationFields,
    };
  }

  return { initialValues, validationFields };
}
